import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { bankAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Landmark, Plus, Trash2, Star, StarOff } from 'lucide-react';
import { toast } from 'sonner';

export default function BankAccountPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ account_holder: '', bank_name: '', account_number: '', ifsc_code: '', account_type: 'savings' });

  const fetchAccounts = async () => {
    try { const { data } = await bankAPI.getAll(); setAccounts(data); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchAccounts(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.account_holder || !form.bank_name || !form.account_number || !form.ifsc_code) {
      toast.error('Please fill all fields'); return;
    }
    setSubmitting(true);
    try {
      await bankAPI.add(form);
      toast.success('Bank account added');
      setShowForm(false);
      setForm({ account_holder: '', bank_name: '', account_number: '', ifsc_code: '', account_type: 'savings' });
      fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add account'); }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    try { await bankAPI.remove(id); toast.success('Account removed'); fetchAccounts(); }
    catch { toast.error('Failed to remove account'); }
  };

  const handleSetPrimary = async (id) => {
    try { await bankAPI.setPrimary(id); toast.success('Primary account updated'); fetchAccounts(); }
    catch { toast.error('Failed to update'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6" data-testid="bank-account-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center">
              <Landmark className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">Bank Accounts</h1>
              <p className="text-xs text-[var(--text-secondary)]">Manage linked bank accounts for withdrawals</p>
            </div>
          </div>
          <button onClick={() => setShowForm(!showForm)} data-testid="add-bank-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all">
            <Plus className="w-4 h-4" /> Add Account
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleAdd} className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-4" data-testid="bank-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Account Holder Name</Label>
                <Input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
                  data-testid="bank-holder" placeholder="Full name as on account"
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Bank Name</Label>
                <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  data-testid="bank-name" placeholder="e.g. State Bank of India"
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Account Number</Label>
                <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  data-testid="bank-number" placeholder="Account number"
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">IFSC Code</Label>
                <Input value={form.ifsc_code} onChange={(e) => setForm({ ...form, ifsc_code: e.target.value.toUpperCase() })}
                  data-testid="bank-ifsc" placeholder="e.g. SBIN0001234"
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10 font-mono uppercase" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[var(--text-secondary)]">Account Type</Label>
              <div className="flex gap-2">
                {['savings', 'current'].map((t) => (
                  <button type="button" key={t} onClick={() => setForm({ ...form, account_type: t })} data-testid={`bank-type-${t}`}
                    className={`px-4 py-2 rounded text-xs font-medium transition-all ${form.account_type === t ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] border border-[var(--border-subtle)]'}`}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-[var(--text-secondary)]">Cancel</button>
              <button type="submit" disabled={submitting} data-testid="bank-submit-btn"
                className="px-5 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all disabled:opacity-50">
                {submitting ? 'Adding...' : 'Add Account'}
              </button>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {loading ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)]">Loading...</div>
          ) : accounts.length === 0 ? (
            <div className="p-8 text-center bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
              <Landmark className="w-10 h-10 mx-auto text-[var(--text-tertiary)] mb-3" />
              <p className="text-sm text-[var(--text-secondary)]">No bank accounts linked yet.</p>
              <p className="text-xs text-[var(--text-tertiary)] mt-1">Add a bank account to enable withdrawals.</p>
            </div>
          ) : (
            accounts.map((a) => (
              <div key={a.id} className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-4 flex items-center gap-4" data-testid={`bank-card-${a.id}`}>
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <Landmark className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[var(--text-primary)]">{a.bank_name}</p>
                    {a.is_primary && <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded">Primary</span>}
                    <span className="text-[10px] bg-[var(--hover-bg)] text-[var(--text-secondary)] px-1.5 py-0.5 rounded capitalize">{a.account_type}</span>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)]">{a.account_holder}</p>
                  <p className="text-xs font-mono text-[var(--text-tertiary)]">****{a.account_number?.slice(-4)} &middot; {a.ifsc_code}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {!a.is_primary && (
                    <button onClick={() => handleSetPrimary(a.id)} data-testid={`bank-primary-${a.id}`}
                      className="p-1.5 rounded hover:bg-[var(--hover-bg)] text-[var(--text-tertiary)] hover:text-amber-400 transition-colors" title="Set as primary">
                      <StarOff className="w-4 h-4" />
                    </button>
                  )}
                  {a.is_primary && <Star className="w-4 h-4 text-amber-400" />}
                  <button onClick={() => handleDelete(a.id)} data-testid={`bank-delete-${a.id}`}
                    className="p-1.5 rounded hover:bg-red-500/10 text-[var(--text-tertiary)] hover:text-red-400 transition-colors" title="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
